import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { color } from 'styles/colors'
import _ from 'underscore'

import Modal from 'components/Modal'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import Brand from 'helpers/brand'

import useDealer from 'hooks/useDealer'
import { useSessionContext } from 'hooks/SessionContext'

import { StackedButtonContainer, screenSize } from 'styles/shared'

import Benefits from 'pages/paywall/Benefits'
import { useFeaturesContext } from 'hooks/FeaturesContext'

type Props = {
  closeCallback: (reloadPage: boolean) => void
}

const UpgradeModal = ({ closeCallback }: Props) => {
  const { session } = useSessionContext()
  const navigate = useNavigate()
  const { dealer, isDealerLoaded } = useDealer()
  const { imageUrl } = dealer
  const { FreeTrial } = useFeaturesContext()

  const onClickClose = () => {
    closeCallback(false)
  }

  const onDealerLogoPress = () => {
    closeCallback(false)
    navigate('/dealer_image')
  }

  let uploadText = 'Upload Dealer Logo'

  const getBenefits = useCallback(() => {
    if (session.isTechnician) {
      return _.select(Benefits.PREMIUM, (benefit) => (benefit !== Benefits.SYSTEM_REPORTS_EMAIL_PREMIUM_BENEFIT && benefit !== Benefits.HISTORICAL_SYSTEM_REPORT_STORAGE_PREMIUM_BENEFIT))
    } else {
      return Benefits.PREMIUM
    }
  }, [session.isTechnician])

  if (isDealerLoaded) {
    if (imageUrl !== null) {
      uploadText = 'Update Dealer Logo'
    }
  } else {
    return
  }

  return (
    <S.Modal variant="sm" isDisplayed closeCallback={() => closeCallback(false)}>
      <S.Content>
        <S.Top>
          <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
          <div />
        </S.Top>

        <S.Success>
          Success!
        </S.Success>
        {FreeTrial &&
          <p> Diagnostics Premium <b style={{ color: color('premiumHighlight') }}>30 Day Free Trial</b> Enabled!</p>
        }
        <p>Everyone on your account now has access to all premium features. Take a moment to get to know Diagnostics’ new features!</p>

        <ul>
          {getBenefits().map(benefit => (
            <S.Explanation key={benefit}>{benefit}</S.Explanation>
          ))}
        </ul>

        <S.StackedButtonContainer noBorder>
          <Button
            decoration="secondary"
            text="OK"
            styles={closeButtonStyle}
            onPress={onClickClose}
          />
          <Button
            decoration="positive"
            text={uploadText}
            styles={uploadButtonStyle}
            onPress={onDealerLogoPress}
          />
        </S.StackedButtonContainer>
      </S.Content>
    </S.Modal>
  )
}

export default UpgradeModal

const buttonFontSize = 14
const buttonHeight = 44
const closeButtonStyle: ButtonStyles = {
  button: {
    borderWidth: 0,
    height: buttonHeight
  },
  text: {
    fontSize: buttonFontSize,
    textTransform: 'none'
  }
}

const uploadButtonStyle: ButtonStyles = {
  button: {
    marginLeft: 15,
    height: buttonHeight
  },
  text: {
    fontSize: buttonFontSize,
    textTransform: 'none'
  }
}

const S = {
  Modal: styled(Modal)`
    height: 100%;
  `,
  Content: styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  Explanation: styled.li`
    margin: 10px 0 0 0;
    padding-right: 5px;
    padding-left: 5px;
  `,
  ProductLogo: styled.img`
    height: 50px;
    margin-bottom: 25px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  Success: styled.div`
    font-size: 18px;
    font-weight: bold;
    color: ${color('hehmModalSuccessText')};
    text-transform: none;
  `,
  Top: styled.div`
    display: flex;
    justify-content: center;
    padding-top: 10px;
  `,
  StackedButtonContainer: styled(StackedButtonContainer)`
    margin-top: auto;
  `
}
